// client/src/pages/About.js
import { Link } from 'react-router-dom';

export default function About() {
  return (
    <div className="min-h-screen bg-cream dark:bg-bg-dark transition-colors duration-300">

      {/* Hero Section — always dark */}
      <div className="relative overflow-hidden bg-royal-900">
        <div className="absolute inset-0 bg-hero-gradient opacity-80"></div>
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 30% 20%, rgba(212,175,55,0.08) 0%, transparent 50%), radial-gradient(ellipse at 70% 80%, rgba(244,208,63,0.05) 0%, transparent 50%)'
        }}></div>
        <div className="relative max-w-3xl mx-auto px-4 pt-24 pb-28 text-center">
          <p className="font-body text-gold-light text-lg mb-4 italic">About the</p>
          <h1 className="font-display text-5xl sm:text-6xl text-gold mb-6 leading-tight">
            Cherry Fairies
          </h1>
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="h-px w-16 bg-gradient-to-r from-transparent via-gold to-transparent"></div>
            <span className="text-gold text-lg">✦</span>
            <div className="h-px w-16 bg-gradient-to-r from-transparent via-gold to-transparent"></div>
          </div>
          <p className="font-display text-2xl text-gold-light mb-6">Make Families Happy</p>
          <p className="text-royal-100 text-lg leading-relaxed max-w-xl mx-auto italic">
            A place where magic is real, wonder is everyday, and the beauty of
            a happy childhood lives on — in stories, in art, and in the small,
            delightful surprises that light up an ordinary Tuesday.
          </p>
        </div>

        {/* Wave curve */}
        <div className="absolute -bottom-px left-0 right-0 pointer-events-none">
          <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full block" preserveAspectRatio="none">
            <path
              d="M0 80L60 72C120 64 240 48 360 40C480 32 600 32 720 36C840 40 960 48 1080 52C1200 56 1320 56 1380 56L1440 56V80H1380C1320 80 1200 80 1080 80C960 80 840 80 720 80C600 80 480 80 360 80C240 80 120 80 60 80H0Z"
              className="fill-cream dark:fill-bg-dark"
            />
          </svg>
        </div>
      </div>

      {/* Origin Story */}
      <div className="max-w-3xl mx-auto px-4 py-16">
        <h2 className="font-display text-2xl text-gold-dark dark:text-gold text-center mb-2">Where It All Began</h2>
             <div className="flex items-center justify-center gap-3 mb-10">
          <div className="w-1.5 h-1.5 rounded-full bg-gold-dark dark:bg-gold"></div>
          <div className="h-px w-12 bg-gold-dark dark:bg-gold"></div>
          <div className="w-1.5 h-1.5 rounded-full bg-gold-dark dark:bg-gold"></div>
        </div>

        <div className="bg-white dark:bg-royal-800/40 border border-royal-200/30 dark:border-royal-600 rounded-xl p-8 relative">

          <p className="text-royal-700 dark:text-royal-100 text-base leading-loose mb-6">
            The Cherry Fairy stories began in the early 90’s when Vivian McConkie Adams — known
            to all who love her as <em className="text-gold-dark dark:text-gold-light">The Purple Grandmother</em> — painted their
            pictures and chronicled their doings in emails sent to her grandchildren.
          </p>

          <div className="border-l-2 border-gold-dark dark:border-gold pl-5 py-1 my-8">
            <p className="text-gold-dark dark:text-gold-light text-lg italic leading-relaxed">
              "Magic is real. There is good magic and it lives and breathes in the
              world around us." ~ The Secret Garden
            </p>
          </div>

          <p className="text-royal-700 dark:text-royal-100 text-base leading-loose mb-6">
            What began as letters to grandchildren grew into something more — a monthly email
            newsletter, and now a full shop of magical digital cards that
            carry the same spirit forward: to make families happy.
          </p>
          <p className="text-royal-700 dark:text-royal-100 text-base leading-loose">
            We believe every person deserves a moment of genuine delight. A pause.
            A breath of something lovely. The feeling of being a child again,
            certain that the world is full of hidden good things.
          </p>
        </div>
      </div>

      {/* Who Are the Cherry Fairies — always dark */}
      <div className="relative overflow-hidden py-16">
        <div className="absolute inset-0 bg-royal-900"></div>
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 20% 30%, rgba(212,175,55,0.1) 0%, transparent 50%), radial-gradient(ellipse at 80% 70%, rgba(192,57,75,0.08) 0%, transparent 50%)'
        }}></div>
        <div className="relative max-w-3xl mx-auto px-4">
          <h2 className="font-display text-2xl text-gold text-center mb-2">Who Are the Cherry Fairies?</h2>
          <p className="text-center text-gold-light/60 italic mb-6">a true account</p>
          <div className="flex items-center justify-center gap-3 mb-10">
            <div className="w-1.5 h-1.5 rounded-full bg-gold-light"></div>
            <div className="h-px w-12 bg-gold-light"></div>
            <div className="w-1.5 h-1.5 rounded-full bg-gold-light"></div>
          </div>

          <p className="text-royal-100 text-base leading-loose mb-6">
            In a tremendous windstorm in 1994, a great pine tree fell in Salt Lake City — and
            with it, the oldest and noblest fairy family in America lost their home. These
            resourceful little creatures soon settled into a gold pot on a mantle in the neighboring
            house, little knowing that the woman who lived there, <em className="text-gold-light">The Purple Grandmother</em>,
            possessed the rare gift of true sight and could actually see them.
          </p>

          <div className="border-l-2 border-gold-light pl-5 py-1 my-8">
            <p className="text-gold-light text-lg italic leading-relaxed">
              Oh the mischief that entered her house that day! Such sights and stories
              as you do not hear in these times.
            </p>
          </div>

          <p className="text-royal-100 text-base leading-loose mb-6">
            The Fairies First announced themselves by jumping on the keys of the computer and spilling out messages. They live to hover around children whom they dearly love.  They show this by giving them lovely dreams and tying their hair into knots at night. The creatures we most talk about are Rose, Lilac, Tulip, and Lilly who are the children of Prosperos and Naillil. Naillil is the tooth fairy. This family has sons also, the eldest of whom is Frederick who has been touring Europe with his tutor Professor Monsieur Troubadour. He is studying history and politics. The fairies are frequently at war with the bustabouts, another line of fairykin from the gnome family. 
           </p>
 
          <p className="text-royal-100 text-base leading-loose mb-6">
The dear creatures love this country which has given them freedom and a prosperous life.  They do keep their titles, Prince, Princess, King, Queen, as it is the family way, and this does have an impact on what or when spells or any magic they are capable of works. They are from the old countries, but they have come here on many ships and in the folds of many cloaks and skirts looking for the pursuit of happiness. This is the American way, and they do not forget those who gave it to us. From time to time they will have an enactment of the very scenes which founded our great fortune.
           </p>

          <p className="text-royal-100 text-base leading-loose mb-6">
            Majebechanda, queen of the fairies, and some of the other fairies of the older generation still remember
            the old days in the old countries where humans, fairies, and magic mixed often.
            When humans invented their own peculiar magic called technology, the fairies went
            into hiding for a time. But now that they've learned to slip through computers and
            dance and play among the circuits, the worlds are mixing once more.
          </p>
          <p className="text-royal-100 text-base leading-loose">
            And you are invited into it.
          </p>
        </div>
      </div>

      {/* What You'll Find Here */}
      <div className="max-w-3xl mx-auto px-4 py-16">
        <h2 className="font-display text-2xl text-gold-dark dark:text-gold text-center mb-2">What You'll Find Here</h2>
        <p className="text-center text-royal-500 dark:text-royal-200 italic mb-6">magic for every occasion</p>
        <div className="flex items-center justify-center gap-3 mb-10">
          <div className="w-1.5 h-1.5 rounded-full bg-gold-dark dark:bg-gold"></div>
          <div className="h-px w-12 bg-gold-dark dark:bg-gold"></div>
          <div className="w-1.5 h-1.5 rounded-full bg-gold-dark dark:bg-gold"></div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-2xl mx-auto">
          <div className="bg-white dark:bg-royal-800/40 border border-royal-200/30 dark:border-royal-600 rounded-xl p-6 relative overflow-hidden">
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-green-500 via-gold to-green-500"></div>
            <h3 className="font-display text-lg text-gold-dark dark:text-gold mb-2">The Stories</h3>
            <p className="text-royal-500 dark:text-royal-200 text-base italic leading-relaxed">
              Classic fairytales and chronicles of the Cherry Fairies
            </p>
          </div>

          <div className="bg-white dark:bg-royal-800/40 border border-royal-200/30 dark:border-royal-600 rounded-xl p-6 relative overflow-hidden">
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gold-gradient"></div>
            <h3 className="font-display text-lg text-gold-dark dark:text-gold mb-2">Magical E-Cards</h3>
            <p className="text-royal-500 dark:text-royal-200 text-base italic leading-relaxed">
              Interactive digital cards full of wonder — to surprise and delight the people you love.
            </p>
          </div>
        </div>
      </div>

      {/* CTA + Contact */}
      <div className="max-w-3xl mx-auto px-4 pb-20 text-center">
        <div className="flex items-center justify-center gap-3 mb-10">
          <div className="w-1.5 h-1.5 rounded-full bg-gold-dark dark:bg-gold"></div>
          <div className="h-px w-20 bg-gold-dark dark:bg-gold"></div>
          <div className="w-1.5 h-1.5 rounded-full bg-gold-dark dark:bg-gold"></div>
          <div className="h-px w-20 bg-gold-dark dark:bg-gold"></div>
          <div className="w-1.5 h-1.5 rounded-full bg-gold-dark dark:bg-gold"></div>
        </div>

        <p className="font-display text-3xl text-gold-dark dark:text-gold mb-5">Come stay a while.</p>
        <p className="text-royal-500 dark:text-royal-100 text-lg leading-relaxed italic max-w-lg mx-auto mb-8">
          We hope you find here what a good story has always given —
          a lightening of burdens, a kindling of hope, and the sudden,
          certain feeling that the world is more beautiful than you remembered.
        </p>

        <div className="flex flex-wrap items-center justify-center gap-4 mb-12">
          <Link
            to="/moonbeams"
            className="px-8 py-3 bg-gold text-royal-900 font-semibold rounded-lg text-base hover:bg-gold-light transition-colors"
          >
            Explore the Cards
          </Link>
          <Link
            to="/stories"
            className="px-8 py-3 border border-gold-dark dark:border-gold text-gold-dark dark:text-gold font-semibold rounded-lg text-base hover:bg-gold/10 transition-colors"
          >
            Read the Stories
          </Link>
        </div>

        {/* Contact Section */}
        <div className="bg-white dark:bg-royal-800/40 border border-royal-200/30 dark:border-royal-600 rounded-xl p-8">
          <h3 className="font-display text-xl text-gold-dark dark:text-gold mb-3">Contact Us</h3>
          <p className="text-royal-500 dark:text-royal-200 text-base mb-4">
            Have a question, a kind word, or a fairy sighting to report? We'd love to hear from you.
          </p>
          <a
            href="mailto:thecherryfairies@gmail.com"
            className="inline-flex items-center gap-2 text-gold-dark dark:text-gold hover:text-gold dark:hover:text-gold-light transition-colors text-lg"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            thecherryfairies@gmail.com
          </a>
        </div>
      </div>

      {/* Footer divider */}
      <div className="border-t border-royal-200/30 dark:border-royal-700 py-6 text-center">
        <p className="text-royal-500 dark:text-royal-200 italic text-base">
          ✦ &nbsp; Make Families Happy &nbsp; ✦
        </p>
      </div>
    </div>
  );
}